// Company mailbox provisioning card.
// Renders mailbox state, address, and HR actions (provision / disable /
// resend credentials) for a person. Polls every 10 s while a job is pending.
// Spec: docs/superpowers/specs/2026-06-07-company-mailbox-provisioning-design.md

import { useState } from 'react';
import { can } from '../auth/store';
import { ConfirmDialog } from './ui';
import {
  useDisableMailbox,
  useMailboxStatus,
  useProvisionMailbox,
  useResendMailboxCredentials,
} from '../api/queries/mailbox';

// Map mailbox state to existing semantic badge classes.
//   none     → badge-new      (neutral — no mailbox yet)
//   pending  → badge-pending  (amber — job in flight)
//   active   → badge-active   (green — live mailbox)
//   disabled → badge-suspended (red group — access revoked)
//   failed   → badge-expired  (red group — last job failed)
const STATE_BADGE: Record<string, string> = {
  none: 'badge-new',
  pending: 'badge-pending',
  active: 'badge-active',
  disabled: 'badge-suspended',
  failed: 'badge-expired',
};

const STATE_LABEL: Record<string, string> = {
  none: 'No mailbox',
  pending: 'Provisioning…',
  active: 'Active',
  disabled: 'Disabled',
  failed: 'Failed',
};

export function MailboxCard({
  personId,
  hasPrimaryPersonalEmail,
}: {
  personId: number;
  /** From the page's already-loaded person detail (emails satellite).
   *  Required by the provision endpoint to send credentials. */
  hasPrimaryPersonalEmail: boolean;
}): JSX.Element {
  const canManage = can('mailbox.manage');
  const { data, isLoading } = useMailboxStatus(personId);
  const provision = useProvisionMailbox(personId);
  const disable = useDisableMailbox(personId);
  const resend = useResendMailboxCredentials(personId);
  const [showProvision, setShowProvision] = useState(false);
  const [address, setAddress] = useState('');
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading || !data) {
    return (
      <section className="detail-card">
        <h2>Company mailbox</h2>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  const lastJob = data.jobs[data.jobs.length - 1];

  return (
    <section className="detail-card">
      <h2>Company mailbox</h2>

      <p>
        <span className={`badge ${STATE_BADGE[data.state] ?? 'badge-new'}`}>
          {STATE_LABEL[data.state] ?? data.state}
        </span>
        {data.address && (
          <>
            {' '}
            <code>{data.address}</code>
          </>
        )}
      </p>

      {data.state === 'failed' && lastJob?.last_error && (
        <p className="error-text">{lastJob.last_error}</p>
      )}

      {data.state === 'active' && (
        <p className="muted">
          {lastJob?.credential_email_sent_at
            ? 'Credentials delivered to the personal address.'
            : 'Credentials not yet delivered.'}
        </p>
      )}

      {error && <p className="error-text">{error}</p>}

      {canManage && (
        <div className="row-actions" style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {(data.state === 'none' ||
            data.state === 'disabled' ||
            data.state === 'failed') && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!hasPrimaryPersonalEmail || provision.isPending}
              title={
                hasPrimaryPersonalEmail
                  ? ''
                  : 'Add a primary personal email first'
              }
              onClick={() => setShowProvision(true)}
            >
              {data.state === 'failed' ? 'Retry provisioning' : 'Provision mailbox'}
            </button>
          )}

          {data.state === 'active' && (
            <>
              {data.credentials_resendable && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={resend.isPending}
                  onClick={() => {
                    setError(null);
                    resend.mutate(undefined, {
                      onError: (e) => setError(e.message),
                    });
                  }}
                >
                  Resend credentials
                </button>
              )}
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={disable.isPending}
                onClick={() => setConfirmDisable(true)}
              >
                Disable mailbox
              </button>
            </>
          )}
        </div>
      )}

      {showProvision && (
        <form
          className="form-grid"
          style={{ marginTop: '0.75rem' }}
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            provision.mutate(
              address.trim() ? { address: address.trim() } : {},
              {
                onSuccess: () => {
                  setShowProvision(false);
                  setAddress('');
                },
                onError: (err) => setError(err.message),
              },
            );
          }}
        >
          <label className="form-field">
            <span>Address (leave blank for first.last@darkspirerisk.com)</span>
            <input
              className="form-input"
              value={address}
              placeholder="auto"
              onChange={(e) => setAddress(e.target.value)}
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={provision.isPending}
            >
              {provision.isPending ? 'Queuing…' : 'Provision'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setShowProvision(false);
                setAddress('');
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={confirmDisable}
        title="Disable mailbox"
        message="Access is revoked by scrambling the password. Mail data stays on the server; full deletion is an ops task."
        confirmLabel="Disable"
        danger
        busy={disable.isPending}
        onConfirm={() => {
          setError(null);
          disable.mutate(undefined, {
            onError: (e) => setError(e.message),
            onSettled: () => setConfirmDisable(false),
          });
        }}
        onCancel={() => setConfirmDisable(false)}
      />
    </section>
  );
}
